import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

/**
 * Show `children` when `allowed`, `fallback` when not, and a "checking access"
 * status while `loading`, so a gated page never flashes the wrong state.
 *
 *   const { loading, isProfessional } = useEntitlements();
 *   <FeatureGate allowed={isProfessional} loading={loading} fallback={<LockedCard … />}>
 *     <Board />
 *   </FeatureGate>
 */
export interface FeatureGateProps {
  allowed: boolean;
  loading?: boolean;
  fallback: ReactNode;
  children: ReactNode;
  /** Shown while `loading`. Default: <GateLoading />. */
  loadingFallback?: ReactNode;
}

export default function FeatureGate({
  allowed,
  loading = false,
  fallback,
  children,
  loadingFallback,
}: FeatureGateProps) {
  if (loading) return <>{loadingFallback ?? <GateLoading />}</>;
  return <>{allowed ? children : fallback}</>;
}

export interface GateLoadingProps {
  /** Fill the viewport, for a route guard that renders before any page chrome. */
  fullScreen?: boolean;
  /** Already-translated text. Default: «جارٍ التحقق من صلاحية الوصول…». */
  label?: string;
}

/** The accessible "checking access" status shared by the gates. */
export function GateLoading({ fullScreen = false, label }: GateLoadingProps) {
  const { t, i18n } = useTranslation('gating');
  return (
    <div
      role="status"
      dir={fullScreen ? i18n.dir() : undefined}
      className={
        fullScreen
          ? 'flex min-h-screen items-center justify-center bg-[var(--pv-color-surface)] px-4'
          : 'flex items-center justify-center px-4 py-12'
      }
    >
      <span className="inline-flex items-center gap-2 text-[0.9375rem] text-[var(--pv-color-text-muted)]">
        <Loader2
          className="h-[1.125rem] w-[1.125rem] shrink-0 animate-spin text-[var(--pv-color-primary)] motion-reduce:animate-none"
          aria-hidden="true"
        />
        {label ?? t('checking')}
      </span>
    </div>
  );
}
